import React, { useState, useRef, useLayoutEffect, useMemo, useEffect } from "react";
import {
  Boxes, Wallet, Layers, AlertTriangle, Clock, RotateCw, CalendarRange, Package, MapPin, Loader2,
  X, Search, Download, ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";

const money = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 }).format(n || 0);
const int = (n) => new Intl.NumberFormat("es-MX").format(n || 0);
const pctStr = (v, total) => (total > 0 ? ((v / total) * 100).toFixed(1) : "0.0");

// Paleta vívida corporativa; se asigna por índice de categoría (diseño aprobado).
const PALETTE = [
  { color: "#1D4ED8", shades: ["#1E40AF", "#1D4ED8", "#2563EB", "#3B82F6"] }, // azul
  { color: "#15803D", shades: ["#166534", "#15803D", "#16A34A", "#22C55E"] }, // verde
  { color: "#EA580C", shades: ["#C2410C", "#EA580C", "#F97316", "#FB923C"] }, // naranja
  { color: "#6D28D9", shades: ["#5B21B6", "#6D28D9", "#7C3AED", "#8B5CF6"] }, // morado
  { color: "#0E7490", shades: ["#155E75", "#0E7490", "#0891B2", "#06B6D4"] }, // cian
  { color: "#B91C1C", shades: ["#991B1B", "#B91C1C", "#DC2626", "#EF4444"] }, // rojo
  { color: "#0F766E", shades: ["#115E59", "#0F766E", "#0D9488", "#14B8A6"] }, // teal
  { color: "#4338CA", shades: ["#3730A3", "#4338CA", "#4F46E5", "#6366F1"] }, // índigo
  { color: "#B45309", shades: ["#92400E", "#B45309", "#D97706", "#F59E0B"] }, // ámbar
  { color: "#BE185D", shades: ["#9D174D", "#BE185D", "#DB2777", "#EC4899"] }, // rosa
];

// ===== Squarified treemap (Bruls et al.) =====
function squarify(items, x, y, width, height) {
  const data = items.map((it) => ({ ...it }));
  const total = data.reduce((s, c) => s + c.value, 0) || 1;
  const scale = (width * height) / total;
  data.forEach((c) => (c._area = c.value * scale));
  const out = [];
  const rect = { x, y, width, height };
  const shortest = (r) => Math.min(r.width, r.height);
  const worst = (row, side) => {
    let max = -Infinity, min = Infinity, sum = 0;
    for (const r of row) { sum += r._area; if (r._area > max) max = r._area; if (r._area < min) min = r._area; }
    const s2 = sum * sum;
    return Math.max((side * side * max) / s2, s2 / (side * side * min));
  };
  const layoutRow = (row) => {
    const sum = row.reduce((s, r) => s + r._area, 0);
    if (rect.width >= rect.height) {
      const colW = sum / rect.height;
      let oy = rect.y;
      for (const r of row) { const rh = r._area / colW; out.push({ ...r, x: rect.x, y: oy, w: colW, h: rh }); oy += rh; }
      rect.x += colW; rect.width -= colW;
    } else {
      const rowH = sum / rect.width;
      let ox = rect.x;
      for (const r of row) { const rw = r._area / rowH; out.push({ ...r, x: ox, y: rect.y, w: rw, h: rowH }); ox += rw; }
      rect.y += rowH; rect.height -= rowH;
    }
  };
  let row = [];
  let i = 0;
  while (i < data.length) {
    const c = data[i];
    const side = shortest(rect);
    if (row.length === 0 || worst([...row, c], side) <= worst(row, side)) { row.push(c); i++; }
    else { layoutRow(row); row = []; }
  }
  if (row.length) layoutRow(row);
  return out;
}

function useMeasure() {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

const HEADER_H = 54;
const GAP = 8;
const GAP_SUB = 4;

function SubTile({ s, parentName, onSelect }) {
  const showValue = s.h - GAP_SUB >= 50 && s.w - GAP_SUB >= 82;
  const showPct = s.h - GAP_SUB >= 72 && s.w - GAP_SUB >= 92;
  return (
    <button
      type="button"
      onClick={() => onSelect({ id: s.id, name: parentName ? `${parentName} › ${s.name}` : s.name })}
      data-testid={`inv-treemap-sub-${s.id}`}
      className="absolute rounded-md overflow-hidden flex flex-col items-center justify-center text-center px-2.5 py-2 text-white cursor-pointer transition-opacity hover:opacity-90"
      style={{ left: s.x + GAP_SUB / 2, top: s.y + GAP_SUB / 2, width: Math.max(0, s.w - GAP_SUB), height: Math.max(0, s.h - GAP_SUB), backgroundColor: s.shade }}
      title={`${s.name} · ${money(s.value)} · ${s.pct}%`}
    >
      <p className="text-white text-[13.5px] font-semibold leading-snug line-clamp-2">{s.name}</p>
      {showValue && <p className="text-white text-[13px] font-bold tabular-nums mt-1 leading-none">{money(s.value)}</p>}
      {showPct && <p className="text-white text-[12px] font-medium mt-0.5 leading-none">({s.pct}%)</p>}
    </button>
  );
}

function CategoryBlock({ c, onSelect }) {
  const [ref, size] = useMeasure();
  const innerH = Math.max(0, size.h - HEADER_H);
  const subs = size.w > 0 && innerH > 0
    ? squarify(c.subs, 0, 0, size.w, innerH)
    : [];
  return (
    <div
      data-testid={`inv-treemap-cat-${c.id}`}
      className="absolute rounded-xl overflow-hidden bg-white shadow-sm"
      style={{ left: c.x + GAP / 2, top: c.y + GAP / 2, width: Math.max(0, c.w - GAP), height: Math.max(0, c.h - GAP) }}
    >
      <button
        type="button"
        onClick={() => onSelect({ id: c.id, name: c.name })}
        data-testid={`inv-treemap-cat-header-${c.id}`}
        className="w-full px-4 flex flex-col justify-center text-white text-left cursor-pointer transition-opacity hover:opacity-90"
        style={{ height: HEADER_H, backgroundColor: c.color }}
        title={`Ver detalle de ${c.name}`}
      >
        <p className="text-white text-[16px] font-extrabold uppercase tracking-wide leading-none truncate">{c.name}</p>
        <p className="text-white text-[15px] font-semibold tabular-nums mt-1.5 leading-none truncate">
          {money(c.value)} <span className="text-white text-[13px] font-medium">({c.pct}%)</span>
        </p>
      </button>
      <div ref={ref} className="relative w-full bg-white" style={{ height: `calc(100% - ${HEADER_H}px)` }}>
        {subs.map((s) => <SubTile key={s.id} s={s} parentName={c.name} onSelect={onSelect} />)}
      </div>
    </div>
  );
}

function Treemap({ categories, onSelect }) {
  const [ref, size] = useMeasure();
  const cats = size.w > 0 && size.h > 0 && categories.length ? squarify(categories, 0, 0, size.w, size.h) : [];
  return (
    <div ref={ref} className="relative w-full h-[560px] md:h-[640px]" data-testid="inv-treemap">
      {cats.map((c) => <CategoryBlock key={c.id} c={c} onSelect={onSelect} />)}
      {!categories.length && (
        <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
          Sin productos con valor para los filtros seleccionados.
        </div>
      )}
    </div>
  );
}

const TONES = {
  blue: "bg-blue-50 text-blue-600", green: "bg-emerald-50 text-emerald-600",
  violet: "bg-violet-50 text-violet-600", orange: "bg-orange-50 text-orange-600",
  amber: "bg-amber-50 text-amber-600", cyan: "bg-cyan-50 text-cyan-600",
};

function Kpi({ k }) {
  const Icon = k.icon;
  return (
    <div data-testid={`inv-kpi-${k.id}`} className="group bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
      <div className="flex items-start gap-3">
        <div className={`shrink-0 p-2.5 rounded-xl ${TONES[k.tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-slate-500 leading-tight">{k.label}</p>
          <p className="text-2xl font-extrabold mt-1.5 tabular-nums truncate text-slate-900">{k.value}</p>
          <p className="text-[11px] text-slate-400 mt-1">{k.sub}</p>
        </div>
      </div>
    </div>
  );
}

function CategoryDetailModal({ cat, total, structureKey, node, warehouse, sucursal, onClose }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PER = 12;
  const clean = (s) => (s || "").replace(/^\[[^\]]*\]\s*/, "");
  const { data, isLoading } = useQuery({
    queryKey: ["inv-detalle", structureKey, node?.id ?? "entire", warehouse, sucursal, cat.id],
    queryFn: async () => (await api.get("/inventarios/costeo/detalle", { params: { structure: structureKey, node_id: node?.id, warehouse, sucursal, category_id: cat.id } })).data,
    enabled: !!cat,
  });
  const items = data?.items || [];
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((i) => (i.ref || "").toLowerCase().includes(t) || (i.name || "").toLowerCase().includes(t));
  }, [items, q]);
  useEffect(() => { setPage(1); }, [q]);
  const pages = Math.max(1, Math.ceil(filtered.length / PER));
  const pageItems = filtered.slice((page - 1) * PER, page * PER);
  const pct = total > 0 ? (((data?.valor_total || 0) / total) * 100).toFixed(1) : "0.0";
  const exportExcel = () => {
    const header = ["Referencia interna", "Producto", "Cantidad", "U.M.", "Costo unitario", "Costo total"];
    const esc = (v) => `<td>${String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`;
    const body = filtered.map((i) => `<tr>${[i.ref, clean(i.name), i.qty, i.uom, i.costo_unit, i.costo_total].map(esc).join("")}</tr>`).join("");
    const html = `<table border="1"><tr>${header.map((h) => `<th>${h}</th>`).join("")}</tr>${body}</table>`;
    const blob = new Blob([`\ufeff<html><head><meta charset="utf-8"></head><body>${html}</body></html>`], { type: "application/vnd.ms-excel" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `costeo_${cat.name}.xls`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const kpis = [["Valor total", money(data?.valor_total)], ["Productos con existencia", int(data?.productos)],
    ["Cantidad total", int(data?.cantidad_total)], ["% del total general", pct + "%"]];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="inv-detalle-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Detalle de categoría</p>
            <h3 className="text-2xl font-extrabold text-slate-900 mt-0.5" data-testid="inv-detalle-title">{cat.name}</h3>
          </div>
          <button onClick={onClose} data-testid="inv-detalle-close" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5">
          {kpis.map(([l, v], i) => (
            <div key={l} className="bg-slate-50 rounded-xl p-3" data-testid={`inv-detalle-kpi-${i}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{l}</p>
              <p className="text-xl font-extrabold text-slate-900 mt-1 tabular-nums truncate">{v}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 px-5 pb-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="inv-detalle-search" placeholder="Buscar por referencia o producto…" className="w-full h-10 pl-9 pr-3 rounded-lg bg-slate-50 border border-slate-200 text-sm" />
          </div>
          <button onClick={exportExcel} data-testid="inv-detalle-export" className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-emerald-700 transition-colors"><Download className="w-4 h-4" />Exportar a Excel</button>
        </div>
        <div className="flex-1 overflow-auto px-5 min-h-[220px]">
          {isLoading ? <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div> : (
            <table className="w-full text-sm" data-testid="inv-detalle-table">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-2">Referencia</th><th className="py-2 px-2">Producto</th>
                  <th className="py-2 px-2 text-right">Cantidad</th><th className="py-2 px-2">U.M.</th>
                  <th className="py-2 px-2 text-right">Costo unitario</th><th className="py-2 px-2 text-right">Costo total</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((i, idx) => (
                  <tr key={`${i.ref}-${idx}`} className="border-b border-slate-100" data-testid={`inv-detalle-row-${idx}`}>
                    <td className="py-2 px-2 font-mono text-xs text-slate-600 whitespace-nowrap">{i.ref}</td>
                    <td className="py-2 px-2 text-slate-800">{clean(i.name)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{int(i.qty)}</td>
                    <td className="py-2 px-2 text-slate-500">{i.uom}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{money(i.costo_unit)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold">{money(i.costo_total)}</td>
                  </tr>
                ))}
                {!pageItems.length && <tr><td colSpan={6} className="py-10 text-center text-slate-400">Sin productos.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between p-5 border-t border-slate-100">
          <p className="text-xs text-slate-500" data-testid="inv-detalle-count">{filtered.length} productos</p>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="inv-detalle-prev" className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm text-slate-600 tabular-nums">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="inv-detalle-next" className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InventarioCosteo() {
  const { currentStructure, structureKey, node } = useGlobal();
  const contexto = node?.path?.length ? node.path.map((p) => p.name).join(" › ") : "Toda la estructura";
  const [warehouse, setWarehouse] = useState("all");
  const [categoria, setCategoria] = useState("all");
  const [sucursal, setSucursal] = useState("all");
  const [selectedCat, setSelectedCat] = useState(null);

  // Al cambiar la Estructura Organizacional (estructura o nodo) resetear filtros.
  const nodeId = node?.id ?? "entire";
  React.useEffect(() => { setWarehouse("all"); setCategoria("all"); setSucursal("all"); setSelectedCat(null); }, [structureKey, nodeId]);

  const { data: filtros } = useQuery({
    queryKey: ["inv-filtros", structureKey, nodeId],
    queryFn: async () => (await api.get("/inventarios/filtros", { params: { structure: structureKey, node_id: node?.id } })).data,
    enabled: !!structureKey,
    staleTime: 5 * 60 * 1000,
  });
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["inv-costeo", structureKey, nodeId, warehouse, categoria, sucursal],
    queryFn: async () => (await api.get("/inventarios/costeo", { params: { structure: structureKey, node_id: node?.id, warehouse, categoria, sucursal } })).data,
    enabled: !!structureKey,
    keepPreviousData: true,
  });

  const total = data?.total || 0;
  const categories = useMemo(() => {
    const cats = data?.categories || [];
    return cats.map((c, i) => {
      const pal = PALETTE[i % PALETTE.length];
      return {
        id: c.id, name: c.name, value: c.value, color: pal.color,
        pct: pctStr(c.value, total),
        subs: c.subs.map((s, j) => ({
          id: s.id, name: s.name, value: s.value, shade: pal.shades[j % pal.shades.length],
          pct: pctStr(s.value, total),
        })),
      };
    });
  }, [data, total]);

  const KPIS = [
    { id: "valor-total", label: "Valor total del inventario", value: money(total), sub: "Costo total valuado", icon: Wallet, tone: "blue" },
    { id: "productos", label: "Productos en inventario", value: int(data?.product_count), sub: "SKUs con valor", icon: Package, tone: "green" },
    { id: "categorias", label: "Categorías activas", value: int(data?.category_count), sub: "Con valor en el período", icon: Layers, tone: "violet" },
    { id: "stock-bajo", label: "Stock bajo", value: "—", sub: "Regla pendiente", icon: AlertTriangle, tone: "orange" },
    { id: "sin-movimiento", label: "Sin movimiento >90 días", value: "—", sub: "Regla pendiente", icon: Clock, tone: "amber" },
    { id: "rotacion", label: "Rotación promedio anual", value: "—", sub: "Regla pendiente", icon: RotateCw, tone: "cyan" },
  ];

  const whOpts = filtros?.warehouses || [];
  const catOpts = filtros?.categories || [];
  const sucOpts = filtros?.sucursales || [];

  const exportTreemapExcel = () => {
    const clean = (s) => (s || "").replace(/^\[[^\]]*\]\s*/, "");
    const esc = (v) => `<td>${String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`;
    const header = ["Categoría", "Subcategoría", "Valor", "% del total"];
    const rows = [];
    categories.forEach((c) => {
      rows.push(`<tr style="font-weight:bold;background:#eef2ff">${[c.name, "", c.value, c.pct + "%"].map(esc).join("")}</tr>`);
      c.subs.forEach((s) => {
        rows.push(`<tr>${[clean(c.name), clean(s.name), s.value, s.pct + "%"].map(esc).join("")}</tr>`);
      });
    });
    rows.push(`<tr style="font-weight:bold;background:#dcfce7">${["TOTAL GENERAL", "", total, "100.0%"].map(esc).join("")}</tr>`);
    const html = `<table border="1"><tr>${header.map((h) => `<th>${h}</th>`).join("")}</tr>${rows.join("")}</table>`;
    const blob = new Blob([`\ufeff<html><head><meta charset="utf-8"></head><body>${html}</body></html>`], { type: "application/vnd.ms-excel" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "costeo_inventario_treemap.xls";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="w-full space-y-6" data-testid="inventario-costeo">
      {/* Encabezado */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Reportes de Inventarios</p>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Reporte de Costeo de Inventario</h1>
        <p className="text-sm text-slate-500 mt-1">Valor del inventario por categoría (Treemap)</p>
      </div>

      {/* Contexto de Estructura Organizacional (solo lectura) */}
      <div className="flex items-center gap-2 text-sm bg-white rounded-2xl border border-slate-200/70 shadow-sm px-4 py-3" data-testid="inv-context">
        <MapPin className="w-4 h-4 text-red-600 shrink-0" />
        <span className="text-slate-400 font-semibold uppercase text-[10px] tracking-widest">Contexto</span>
        <span className="font-semibold text-slate-800 truncate">{currentStructure?.name || "—"}</span>
        <span className="text-slate-300">·</span>
        <span className="text-slate-600 truncate">{contexto}</span>
      </div>

      {/* Filtros internos del reporte */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4" data-testid="inv-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Sucursal</label>
            <Select value={sucursal} onValueChange={setSucursal}>
              <SelectTrigger className="h-10 mt-1 bg-slate-50 border-slate-200 text-sm text-slate-700" data-testid="inv-filter-sucursal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Todas</SelectItem>
                {sucOpts.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Almacén</label>
            <Select value={warehouse} onValueChange={setWarehouse}>
              <SelectTrigger className="h-10 mt-1 bg-slate-50 border-slate-200 text-sm text-slate-700" data-testid="inv-filter-almacen">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Todos</SelectItem>
                {whOpts.map((w) => (
                  <SelectItem key={w.id} value={String(w.id)}>{w.name}{w.code ? ` (${w.code})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Categoría</label>
            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger className="h-10 mt-1 bg-slate-50 border-slate-200 text-sm text-slate-700" data-testid="inv-filter-categoria">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Todas</SelectItem>
                {catOpts.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Rango de fechas</label>
            <div data-testid="inv-filter-fechas" className="h-10 mt-1 flex items-center gap-2 px-3 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-700">
              <CalendarRange className="w-4 h-4 text-slate-400 shrink-0" />
              <span className="truncate">01 ago 2026 – 28 ago 2026</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tarjetas KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4" data-testid="inv-kpis">
        {KPIS.map((k) => <Kpi key={k.id} k={k} />)}
      </div>

      {/* Treemap */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="inv-treemap-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-xl bg-red-50 text-red-600"><Boxes className="w-4 h-4" /></span>
            <h2 className="text-base md:text-lg font-bold text-slate-900">Valor del inventario por categoría (Treemap)</h2>
            {isFetching && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total general</p>
              <p className="text-lg font-extrabold text-slate-900 tabular-nums" data-testid="inv-treemap-total">{money(total)}</p>
            </div>
            <button
              onClick={exportTreemapExcel}
              disabled={!categories.length}
              data-testid="inv-treemap-export"
              className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-emerald-700 transition-colors disabled:opacity-40"
            >
              <Download className="w-4 h-4" />Exportar a Excel
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 whitespace-nowrap">Ver como:</span>
              <Select defaultValue="valor">
                <SelectTrigger className="h-9 w-28 bg-slate-50 border-slate-200 text-sm" data-testid="inv-treemap-vercomo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="valor">Valor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        {isLoading
          ? <div className="h-[560px] md:h-[640px] flex items-center justify-center"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div>
          : <Treemap categories={categories} onSelect={setSelectedCat} />}
      </div>

      {selectedCat && (
        <CategoryDetailModal
          cat={selectedCat}
          total={total}
          structureKey={structureKey}
          node={node}
          warehouse={warehouse}
          sucursal={sucursal}
          onClose={() => setSelectedCat(null)}
        />
      )}
    </div>
  );
}
